import { api } from '../core/client';

export interface ApiCoupon {
  id: string;
  code: string;
  desc: string;
  type: 'pct' | 'flat' | 'item' | 'category';
  typeLabel: string;
  value: string;
  minOrder: string;
  used: number;
  active: boolean;
}

export interface CreateCouponPayload {
  code: string;
  desc?: string;
  type: 'pct' | 'flat' | 'item' | 'category';
  typeLabel?: string;
  value: string;
  minOrder?: string;
  active: boolean;
}

export interface ApiRedemption {
  code: string;
  name: string;
  mobile: string;
  bill: string;
  disc: string;
  time: string;
}

/** Result of validating a coupon against an order subtotal (server-computed discount). */
export interface ApiCouponValidateResult {
  valid: boolean;
  code: string;
  discount: number;
  message: string;
}

interface ApiEnvelope<T> {
  success: boolean;
  message: string | null;
  result: T;
}

/** Coupons — GET /api/coupon (list) + POST /api/coupon (create). */
export const couponService = {
  list(): Promise<ApiCoupon[]> {
    return api.get<ApiEnvelope<ApiCoupon[]>>('/coupon').then((r) => r.data.result);
  },
  create(payload: CreateCouponPayload): Promise<ApiCoupon> {
    return api.post<ApiEnvelope<ApiCoupon>>('/coupon', payload).then((r) => r.data.result);
  },
  /** Validate a code against an order subtotal → { valid, discount, message }. */
  validate(code: string, subTotal: number): Promise<ApiCouponValidateResult> {
    return api
      .get<ApiEnvelope<ApiCouponValidateResult>>('/coupon/validate', { params: { code, subtotal: subTotal } })
      .then((r) => r.data.result);
  },
  /** Turn a coupon on/off — persists (this used to be local state only). */
  setActive(id: string, active: boolean): Promise<void> {
    return api.post(`/coupon/${encodeURIComponent(id)}/active`, { active }).then(() => undefined);
  },
  remove(id: string): Promise<void> {
    return api.delete(`/coupon/${encodeURIComponent(id)}`).then(() => undefined);
  },
  /** Total discount given by redemptions this month (₹) — was a hardcoded figure. */
  monthDiscount(): Promise<number> {
    return api.get<ApiEnvelope<number>>('/coupon/month-discount').then((r) => r.data.result ?? 0);
  }
};

/** Coupon redemptions — GET /api/redemption. */
export const redemptionService = {
  list(): Promise<ApiRedemption[]> {
    return api.get<ApiEnvelope<ApiRedemption[]>>('/redemption').then((r) => r.data.result);
  }
};
